/**
 * Event Details Enhanced component - Detailed event view with real items from database
 * WITH IMAGE CAROUSEL - Shows all 1-3 images per item
 * ITEMS CLICKABLE - Click to view full item details
 * FIXED: Check participatingUsers on event load to restore join status on refresh
 * UPDATED: Display userName instead of firstName/lastName
 * UPDATED: Removed email display for privacy
 * v1.15.0: Added EventItemsSection for "Add Items to Events" feature
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  Chip,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { AppDispatch, RootState } from '../store/store';
import { joinEventAsync, leaveEventAsync } from '../store/slices/authSlice';
import { fetchEventDetails, type EventDetailsResponse } from '../services/userService';
import EventItemsSection from './EventItemsSection';
import MathTradeRunPanel from './MathTradeRunPanel';
import EventLocationsManager from './EventLocationsManager';
import { EventAdminSummaryPanel, EventBehaviorPanel, EventCancelSessionDialog, EventCheckInQrPanel, EventCompletionPanel, EventDetailsDialog, EventInfoGrid, EventLocationsOverviewPanel, EventMessagingPanel, EventParticipantsPanel, EventRemindersPanel, EventSessionsPanel, EventStartSessionPanel, EventTimelinePanel, EventTradeGroupsPanel, EventWatchListPanel, MathTradeSnapshot, TradeGroupPathsPanel } from './EventOverviewSections';
import { buildEventsApiUrl } from '../config/api';
import { normalizeImageUrl } from '../utils/images';
import { DEFAULT_RUNTIME_SETTINGS, getRuntimeSettings } from '../services/runtimeSettings';
import { isSiteAdminUser } from '../config/admin';
import { getAuthUserId } from '../utils/authUser';
import { buildAuthHeaders, buildJsonAuthHeaders } from '../utils/authFetch';
import QRCode from 'qrcode';

const getAdminTradePollingIntervalMs = (tradeGroups: any[], activeSessions: any[], runtimeSettings = DEFAULT_RUNTIME_SETTINGS) => {
  const polling = runtimeSettings.eventDayPolling;
  if (activeSessions.some((session) => ['staged', 'active'].includes(String(session?.status || '')))) return polling.adminActivePollMs;
  if (tradeGroups.some((group) => ['ready-to-start-trade', 'in-session'].includes(String(group?.readinessState || '')))) return polling.adminReadyPollMs;
  return polling.adminIdlePollMs;
};

interface EventData extends EventDetailsResponse {
  participatingUsers?: string[];
  tradeDateTime?: string;
  tradeCutoffDate?: string;
}

interface ParticipantSummary {
  userId: string;
  displayName: string;
  userName?: string;
  avatarUrl?: string | null;
  profileTag?: string | null;
  joinedAt?: string | null;
  completedTradeCount?: number;
  reviewCount?: number;
  trustScore?: number | null;
}

interface EventTimelineEntry {
  id: string;
  type: string;
  message: string;
  createdAt?: string | null;
  actorUserID?: string | null;
  subjectUserID?: string | null;
  actorDisplayName?: string | null;
  subjectDisplayName?: string | null;
  itemID?: string | null;
  itemName?: string | null;
  runID?: string | null;
  metadata?: Record<string, any>;
}

interface EventTradeGroupRecord {
    id: string;
    groupKey: string;
    participantCount: number;
    arrivedCount: number;
    busyCount: number;
    readinessState: string;
    status?: string;
    participantUserIds?: string[];
    participantStates?: Array<{
      userId: string;
      checkedInAt?: string | null;
      currentAvailability?: string | null;
      activeSessionId?: string | null;
      lastLocationId?: string | null;
    }>;
    tradeEdges?: Array<{
      fromLabel?: string;
      toLabel?: string;
      receiveLabel?: string;
      giveItem?: string;
      receiveItem?: string;
    }>;
  }

interface EventLocationOption {
    id: string;
    name: string;
    capacity: number;
    active: boolean;
    currentSessionId?: string | null;
    occupiedAt?: string | null;
  }

interface ActiveTradeSession {
    id: string;
    tradeGroupId: string;
    locationName?: string;
    status: string;
    initiatedAt?: string;
    startedAt?: string | null;
    participantUserIds?: string[];
    canceledAt?: string | null;
    completedAt?: string | null;
    cancelReasonCode?: string | null;
    cancelReasonText?: string | null;
    participantStates?: Array<{
      userId: string;
      locationCheckedInAt?: string | null;
      completionConfirmedAt?: string | null;
      issueReportedAt?: string | null;
      issueReasonCode?: string | null;
      issueReasonText?: string | null;
    }>;
    reviewAssignments?: Array<{
      userId: string;
      reviewTargetUserId?: string | null;
      giveItem?: string | null;
      receiveItem?: string | null;
    }>;
  }

interface TradeDayAdminSnapshot {
  tradeGroups?: EventTradeGroupRecord[];
  locations?: EventLocationOption[];
  sessions?: ActiveTradeSession[];
  behaviorRecords?: any[];
  reviews?: any[];
}

type TimelineFilter = 'all' | 'participants' | 'items' | 'messaging' | 'math-trade' | 'event';
type EventDetailsTab = 'overview' | 'trade-day';

const formatEventDateTime = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatJoinedDate = (value?: string | null) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const hasTradeCutoffPassed = (event?: EventData | null) => {
  if (!event) return false;
  const cutoffAt = event.tradeCutoffDate || event.tradeDateTime;
  if (!cutoffAt) return false;
  const parsed = new Date(cutoffAt);
  if (Number.isNaN(parsed.getTime())) return false;
  return Date.now() >= parsed.getTime();
};

const formatElapsed = (from?: string | null) => {
  if (!from) return '—';
  const started = new Date(from).getTime();
  if (Number.isNaN(started)) return '—';
  const diffMs = Math.max(0, Date.now() - started);
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const describeTradeGroupBlocker = (group: EventTradeGroupRecord) => {
  if (group.status === 'completed') return 'This trade group already completed successfully.';
  if (group.status === 'in-session') return 'This trade group is currently in an active session.';
  if (group.status === 'pending' && group.readinessState === 'ready-to-start-trade') return 'This group is fully ready to start.';
  if (group.status === 'pending' && group.readinessState === 'ready-when-other-members-finish-other-trades') return 'Everyone has arrived, but at least one participant is still busy in another trade session.';
  return 'One or more participants have not checked in at the event yet.';
};

const timelineTypeLabels: Record<string, string> = {
  'event-created': 'Event created',
  'event-updated': 'Event updated',
  'participant-joined': 'Participant joined',
  'participant-left': 'Participant left',
  'items-added': 'Items added',
  'item-removed': 'Item removed',
  'participants-messaged': 'Message sent',
  'math-trade-preview-run': 'Preview generated',
  'math-trade-published': 'Preview published',
  'math-trade-finalized': 'Math trade finalized'
};

const timelineTypeToFilter: Record<string, TimelineFilter> = {
  'event-created': 'event',
  'event-updated': 'event',
  'participant-joined': 'participants',
  'participant-left': 'participants',
  'items-added': 'items',
  'item-removed': 'items',
  'participants-messaged': 'messaging',
  'math-trade-preview-run': 'math-trade',
  'math-trade-published': 'math-trade',
  'math-trade-finalized': 'math-trade'
};

interface EventItemRecord {
  id: string;
  name: string;
  category: string;
  description?: string;
  images?: string[];
  tags?: string[];
  addedByUserId?: string;
  addedDate?: string;
  createdByUserId?: string;
  userID?: string;
}

const EventDetailsEnhanced: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [event, setEvent] = useState<EventData | null>(null);  const [loading, setLoading] = useState(true);  const [error, setError] = useState<string | null>(null);
  const [joinLoading, setJoinLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<EventDetailsTab>('overview');
  const [reminderOptions, setReminderOptions] = useState<number[]>([]);
  const [selectedReminderOffsets, setSelectedReminderOffsets] = useState<number[]>([]);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [mathTradeSummary, setMathTradeSummary] = useState<{ totalItems: number; totalMyItems: number; totalWithPreferences: number; inputState?: string; publishedIsFinal?: boolean; publishedVisible?: boolean; previewVisible?: boolean; tradeCutoffPassed?: boolean; publishedAt?: string | null; lockedAt?: string | null } | null>(null);
  const [eventItems, setEventItems] = useState<EventItemRecord[]>([]);
  const [eventItemsLoading, setEventItemsLoading] = useState(false);
  const [eventItemsError, setEventItemsError] = useState<string | null>(null);
  const [participantMessageSubject, setParticipantMessageSubject] = useState('');
  const [participantMessageBody, setParticipantMessageBody] = useState('');
  const [participantMessageSaving, setParticipantMessageSaving] = useState(false);
  const [participantMessageError, setParticipantMessageError] = useState<string | null>(null);
  const [participantMessageSuccess, setParticipantMessageSuccess] = useState<string | null>(null);
  const [participantSummaries, setParticipantSummaries] = useState<ParticipantSummary[]>([]);
  const [participantSummaryLoading, setParticipantSummaryLoading] = useState(false);
  const [participantSummaryError, setParticipantSummaryError] = useState<string | null>(null);
  const [participantsExpanded, setParticipantsExpanded] = useState(false);
  const [eventQrDataUrl, setEventQrDataUrl] = useState<string | null>(null);
  const [eventQrExpanded, setEventQrExpanded] = useState(false);
  const [timelineEntries, setTimelineEntries] = useState<EventTimelineEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('all');
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [behaviorExpanded, setBehaviorExpanded] = useState(false);
  const [messagingExpanded, setMessagingExpanded] = useState(false);
  const [completionExpanded, setCompletionExpanded] = useState(false);
  const [tradeGroups, setTradeGroups] = useState<EventTradeGroupRecord[]>([]);
  const [tradeGroupsLoading, setTradeGroupsLoading] = useState(false);
  const [tradeGroupsError, setTradeGroupsError] = useState<string | null>(null);
  const suppressTradeGroupsOwnerOnlyError = Boolean(
    tradeGroupsError && tradeGroupsError.toLowerCase().includes('only the event owner can manage locations')
  );
  const [locations, setLocations] = useState<EventLocationOption[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveTradeSession[]>([]);
  const [sessionActionError, setSessionActionError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [sessionSaving, setSessionSaving] = useState(false);
  const [eventCompletionError, setEventCompletionError] = useState<string | null>(null);
  const [eventCompleting, setEventCompleting] = useState(false);
  const [behaviorSubjectUserId, setBehaviorSubjectUserId] = useState('');
  const [behaviorOutcomeType, setBehaviorOutcomeType] = useState('successful-handoff-confirmed');
  const [behaviorNotes, setBehaviorNotes] = useState('');
  const [behaviorSaving, setBehaviorSaving] = useState(false);
  const [behaviorRecords, setBehaviorRecords] = useState<any[]>([]);
  const [behaviorSessionId, setBehaviorSessionId] = useState('');
  const [selectedSessionDetails, setSelectedSessionDetails] = useState<ActiveTradeSession | null>(null);
  const [eventReviews, setEventReviews] = useState<any[]>([]);
  const [runtimeSettings, setRuntimeSettings] = useState(DEFAULT_RUNTIME_SETTINGS);
  const [cancelDialogSessionId, setCancelDialogSessionId] = useState<string | null>(null);
  const [cancelReasonCode, setCancelReasonCode] = useState('other');
  const [cancelReasonText, setCancelReasonText] = useState('');
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [cleanupDrafts, setCleanupDrafts] = useState<Record<string, { reasonCode: string; reasonText: string }>>({});
  const [sessionBehaviorSubjectUserId, setSessionBehaviorSubjectUserId] = useState('');
  const [sessionBehaviorOutcomeType, setSessionBehaviorOutcomeType] = useState('successful-handoff-confirmed');
  const [sessionBehaviorNotes, setSessionBehaviorNotes] = useState('');
  const [sessionDetailTab, setSessionDetailTab] = useState(0);
  const [selectedTradeGroupDetails, setSelectedTradeGroupDetails] = useState<EventTradeGroupRecord | null>(null);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState<EventLocationOption | null>(null);
  const tradeGroupsRef = useRef<EventTradeGroupRecord[]>([]);
  const activeSessionsRef = useRef<ActiveTradeSession[]>([]);
  
  // Track if user has joined (check from database on load)
  const [userHasJoined, setUserHasJoined] = useState(false);
  // Get userId from Redux auth state
  const userId = getAuthUserId(user as any);
  const tradeCutoffPassed = hasTradeCutoffPassed(event);
  const printableQrTitle = useMemo(() => {
    if (!eventId) return 'Event';
    const label = event?.eventName ? `Event: ${event.eventName}` : 'Event';
    return `${label}\nID: ${eventId}`;
  }, [event?.eventName, eventId]);
  const eventQrTargetUrl = useMemo(() => {
    if (!eventId) return '';
    return `https://crowdtradesdev.kenwayne.me/#/events/${encodeURIComponent(eventId)}/check-in`;
  }, [eventId]);
  const participantLookup = useMemo(() => participantSummaries.reduce((acc, participant) => {
    acc[participant.userId] = participant.displayName || participant.userId;
    return acc;
  }, {} as Record<string, string>), [participantSummaries]);
  const filteredTimelineEntries = useMemo(() => {
    if (timelineFilter === 'all') return timelineEntries;
    return timelineEntries.filter((entry) => timelineTypeToFilter[entry.type] === timelineFilter);
  }, [timelineEntries, timelineFilter]);
  const actionableTradeGroups = useMemo(() => tradeGroups.filter((group) => group.status !== 'completed' && group.readinessState !== 'completed'), [tradeGroups]);
  const eventDaySummary = useMemo(() => ({
    readyGroups: actionableTradeGroups.filter((group) => group.readinessState === 'ready-to-start-trade').length,
    waitingGroups: actionableTradeGroups.filter((group) => group.readinessState !== 'ready-to-start-trade').length,
    stagedSessions: activeSessions.filter((session) => session.status === 'staged').length,
    activeSessions: activeSessions.filter((session) => session.status === 'active').length,
    completedSessions: activeSessions.filter((session) => session.status === 'completed').length,
    canceledSessions: activeSessions.filter((session) => session.status === 'canceled').length,
    behaviorRecords: behaviorRecords.length,
    noShows: behaviorRecords.filter((record) => record.outcomeType === 'no-show').length,
    lateArrivals: behaviorRecords.filter((record) => record.outcomeType === 'late-arrival').length,
    wrongItems: behaviorRecords.filter((record) => record.outcomeType === 'wrong-item-delivered').length,
  }), [actionableTradeGroups, activeSessions, behaviorRecords]);
  const eventDayAlerts = useMemo(() => {
    const alerts: Array<{ severity: 'warning' | 'info' | 'error'; message: string; actionLabel?: string; onClick?: () => void }> = [];
    const readyGroups = actionableTradeGroups.filter((group) => group.readinessState === 'ready-to-start-trade');
    if (readyGroups.length > 0) {
      alerts.push({ severity: 'info', message: `${readyGroups.length} trade group(s) are ready to start.`, actionLabel: 'Review Groups', onClick: () => window.scrollTo({ top: document.body.scrollHeight / 2, behavior: 'smooth' }) });
    }
    const issueRecords = behaviorRecords.filter((record) => ['wrong-item-delivered', 'item-condition-dispute', 'admin-resolved-issue'].includes(record.outcomeType));
    if (issueRecords.length > 0) {
      const relatedSession = activeSessions.find((session) => session.id === issueRecords[0]?.sessionId);
      alerts.push({ severity: 'warning', message: `${issueRecords.length} behavior/issue record(s) need admin awareness.`, actionLabel: relatedSession ? 'Open Session' : undefined, onClick: relatedSession ? () => { setSessionDetailTab(1); setSelectedSessionDetails(relatedSession); } : undefined });
    }
    const noShows = behaviorRecords.filter((record) => record.outcomeType === 'no-show');
    if (noShows.length > 0) {
      const relatedSession = activeSessions.find((session) => session.id === noShows[0]?.sessionId);
      alerts.push({ severity: 'warning', message: `${noShows.length} no-show record(s) were logged.`, actionLabel: relatedSession ? 'Open Session' : undefined, onClick: relatedSession ? () => { setSessionDetailTab(1); setSelectedSessionDetails(relatedSession); } : undefined });
    }
    const stalledSessions = activeSessions.filter((session) => session.status === 'staged');
    if (stalledSessions.length > 0) {
      alerts.push({ severity: 'info', message: `${stalledSessions.length} staged session(s) may still be waiting on arrivals.`, actionLabel: 'Open Oldest Session', onClick: () => { setSessionDetailTab(0); setSelectedSessionDetails(stalledSessions[0]); } });
    }
    return alerts;
  }, [actionableTradeGroups, activeSessions, behaviorRecords]);
  const handleLocationsChanged = useCallback((nextLocations: EventLocationOption[]) => {
    setLocations((current) => {
      const currentSerialized = JSON.stringify(current);
      const nextSerialized = JSON.stringify(nextLocations);
      return currentSerialized === nextSerialized ? current : nextLocations;
    });
  }, []);

  const selectedTradeGroup = useMemo(() => tradeGroups.find((group) => group.id === selectedGroupId) || null, [tradeGroups, selectedGroupId]);

  const loadEventItems = useCallback(async () => {
    if (!eventId) {
      setEventItems([]);
      setEventItemsError('No event ID provided');
      return;
    }

    try {
      setEventItemsLoading(true);
      setEventItemsError(null);
      const itemsQuery = userId ? `?userId=${encodeURIComponent(userId)}` : '';
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items${itemsQuery}`), {
        method: 'GET',
        headers: userId ? buildAuthHeaders(userId) : undefined
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to fetch items (${response.status})`);
      }

      if (data.success && Array.isArray(data.items)) {
        setEventItems(data.items);
      } else {
        throw new Error(data.error || 'Failed to load items');
      }
    } catch (itemsErr: any) {
      console.error('❌ Error loading event items:', itemsErr);
      setEventItems([]);
      setEventItemsError(itemsErr.message || 'Failed to load items');
    } finally {
      setEventItemsLoading(false);
    }
  }, [eventId, userId]);

  const loadMathTradeSummary = useCallback(async () => {
    if (!eventId || !userId) {
      setMathTradeSummary(null);
      return;
    }

    try {
      const statusResponse = await fetch(buildEventsApiUrl(`/api/events/${eventId}/math-trade/status`), {
        headers: buildAuthHeaders(userId),
      });
      if (!statusResponse.ok) return;

      const statusData = await statusResponse.json();
      const lifecycle = statusData.mathTrade || {};
      const userSummary = lifecycle.userSummary || {};

      setMathTradeSummary({
        totalItems: Number.isFinite(userSummary.totalItems) ? userSummary.totalItems : Number(lifecycle.itemCount || 0),
        totalMyItems: Number.isFinite(userSummary.totalMyItems) ? userSummary.totalMyItems : 0,
        totalWithPreferences: Number.isFinite(userSummary.totalWithPreferences) ? userSummary.totalWithPreferences : 0,
        ...(lifecycle as any)
      });
    } catch (summaryError) {
      console.warn('⚠️ Failed to load math trade summary', summaryError);
    }
  }, [eventId, userId]);

  const handleItemsUpdated = useCallback(async () => {
    await Promise.all([
      loadEventItems(),
      loadMathTradeSummary(),
    ]);
  }, [loadEventItems, loadMathTradeSummary]);

  useEffect(() => {
    tradeGroupsRef.current = tradeGroups;
  }, [tradeGroups]);

  useEffect(() => {
    activeSessionsRef.current = activeSessions;
  }, [activeSessions]);

  const applyTradeDayAdminSnapshot = useCallback((snapshot?: TradeDayAdminSnapshot | null) => {
    if (!snapshot) return;
    setTradeGroups(Array.isArray(snapshot.tradeGroups) ? snapshot.tradeGroups : []);
    setLocations(Array.isArray(snapshot.locations) ? snapshot.locations : []);
    setActiveSessions(Array.isArray(snapshot.sessions) ? snapshot.sessions : []);
    setBehaviorRecords(Array.isArray(snapshot.behaviorRecords) ? snapshot.behaviorRecords : []);
    setEventReviews(Array.isArray(snapshot.reviews) ? snapshot.reviews : []);
  }, []);

  const loadTradeDayAdminSnapshot = useCallback(async (silent = false) => {
    if (!eventId || !userId || !event || !(event.ownerID === userId || isSiteAdminUser(user as any))) {
      setTradeGroups([]);
      setLocations([]);
      setActiveSessions([]);
      setBehaviorRecords([]);
      setEventReviews([]);
      return;
    }

    if (!silent) {
      setTradeGroupsLoading(true);
    }

    try {
      setTradeGroupsError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/trade-day/admin-snapshot`), {
        headers: buildAuthHeaders(userId),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load trade-day admin data');
      }
      applyTradeDayAdminSnapshot(data.snapshot);
    } catch (err: any) {
      setTradeGroupsError(err.message || 'Failed to load trade-day admin data');
    } finally {
      if (!silent) {
        setTradeGroupsLoading(false);
      }
    }
  }, [applyTradeDayAdminSnapshot, eventId, event, user, userId]);

  const suggestedLocations = useMemo(() => {
    if (!selectedTradeGroup) return locations.filter((location) => location.active);
    return [...locations]
      .filter((location) => location.active)
      .sort((a, b) => {
        const aUnavailable = a.currentSessionId ? 1 : 0;
        const bUnavailable = b.currentSessionId ? 1 : 0;
        if (aUnavailable !== bUnavailable) return aUnavailable - bUnavailable;
        const aFit = Math.abs((a.capacity || 0) - selectedTradeGroup.participantCount);
        const bFit = Math.abs((b.capacity || 0) - selectedTradeGroup.participantCount);
        return aFit - bFit;
      });
  }, [locations, selectedTradeGroup]);

  // Load event details and items together
  useEffect(() => {
    const loadEventData = async () => {
      if (!eventId) {
        setError('No event ID provided');
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching event details for:', eventId);
      
      try {
        setLoading(true);
        const [eventData] = await Promise.all([
          fetchEventDetails(eventId),
          loadEventItems()
        ]);

        if (eventData) {
          console.log('✅ Event loaded:', eventData.eventName);
          setEvent(eventData);

          const joined = Boolean(isAuthenticated && user && (eventData.participatingUsers || []).includes(userId));
          if (joined) {
            console.log('👤 Checking join status: userId=' + userId + ', participatingUsers=' + (eventData.participatingUsers || []).length + ', hasJoined=true');
            setUserHasJoined(true);
          } else {
            setUserHasJoined(false);
          }
        } else {
          setError('Could not load event details');
        }
      } catch (err: any) {
        console.error('❌ Error loading event:', err);
        setError(err.message || 'Failed to load event details');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, isAuthenticated, loadEventItems, user, userId]);

  useEffect(() => {
    if (activeTab === 'trade-day' && (!event || !userId || !(event.ownerID === userId || isSiteAdminUser(user as any)))) {
      setActiveTab('overview');
    }
  }, [activeTab, event, user, userId]);

  useEffect(() => {
    const buildEventQr = async () => {
      if (!eventId) {
        setEventQrDataUrl(null);
        return;
      }

      try {
        const nextDataUrl = await QRCode.toDataURL(eventQrTargetUrl || eventId, {
          width: 320,
          margin: 2,
          color: {
            dark: '#111827',
            light: '#FFFFFF',
          },
        });
        setEventQrDataUrl(nextDataUrl);
      } catch (qrError) {
        console.error('Failed to build event QR code', qrError);
        setEventQrDataUrl(null);
      }
    };

    buildEventQr();
  }, [eventId, eventQrTargetUrl]);

  const handleDownloadEventQr = () => {
    if (!eventQrDataUrl || !eventId) return;
    const link = document.createElement('a');
    link.href = eventQrDataUrl;
    link.download = `event-${eventId}-qr.png`;
    link.click();
  };

  const handleOpenPrintableQr = () => {
    if (!eventQrDataUrl || !eventId) return;

    const escapedTitle = printableQrTitle
      .split('\n')
      .map((line) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
      .join('<br/>');

    const printableHtml = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Event QR - ${eventId}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; padding: 32px; color: #111827; text-align: center; }
      .sheet { max-width: 700px; margin: 0 auto; }
      img { width: 360px; max-width: 100%; height: auto; }
      .title { font-size: 28px; font-weight: 700; margin-bottom: 12px; }
      .id { font-size: 16px; color: #4b5563; margin-top: 16px; }
      @media print { body { padding: 16px; } }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="title">${escapedTitle}</div>
      <img src="${eventQrDataUrl}" alt="Event QR code" />
      <div class="id">Embedded Event ID: ${eventId}</div>
    </div>
    <script>
      window.addEventListener('load', function () {
        setTimeout(function () { window.print(); }, 150);
      });
    </script>
  </body>
</html>`;

    const blob = new Blob([printableHtml], { type: 'text/html' });
    const printUrl = URL.createObjectURL(blob);
    window.open(printUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(printUrl), 60000);
  };

  useEffect(() => {
    const loadTimeline = async () => {
      if (!eventId || !userId || !event || !(event.ownerID === userId || isSiteAdminUser(user as any))) {
        setTimelineEntries([]);
        return;
      }

      try {
        setTimelineLoading(true);
        setTimelineError(null);
        const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/timeline`), {
          headers: buildAuthHeaders(userId),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load event timeline');
        }
        setTimelineEntries(Array.isArray(data.timeline) ? data.timeline : []);
      } catch (timelineErr: any) {
        setTimelineError(timelineErr.message || 'Failed to load event timeline');
      } finally {
        setTimelineLoading(false);
      }
    };

    loadTimeline();
  }, [eventId, user, userId, event]);

  useEffect(() => {
    const loadReminderPreferences = async () => {
      if (!eventId || !userId || !event) return;

      const canManageReminders = event.ownerID === userId || (event.participatingUsers || []).includes(userId);
      if (!canManageReminders) {
        setReminderOptions([]);
        setSelectedReminderOffsets([]);
        return;
      }

      try {
        setReminderError(null);
        const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/notification-preferences/${userId}`), {
          headers: buildJsonAuthHeaders(userId)
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load reminder preferences');
        }

        setReminderOptions(data.preferences?.availableDayOffsets || []);
        setSelectedReminderOffsets(data.preferences?.selectedDayOffsets || []);
      } catch (err: any) {
        console.error('❌ Error loading reminder preferences:', err);
        setReminderError(err.message || 'Failed to load reminder preferences');
      }
    };

    loadReminderPreferences();
  }, [eventId, userId, event]);

  useEffect(() => {
    let cancelled = false;

    getRuntimeSettings().then((settings) => {
      if (!cancelled) {
        setRuntimeSettings(settings);
      }
    });

    loadTradeDayAdminSnapshot();
    const intervalId = window.setInterval(() => {
      loadTradeDayAdminSnapshot(true);
    }, getAdminTradePollingIntervalMs(tradeGroupsRef.current, activeSessionsRef.current, runtimeSettings));

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [loadTradeDayAdminSnapshot, runtimeSettings]);

  const handleStartTradeSession = async () => {
    if (!eventId || !userId || !selectedGroupId || !selectedLocationId) return;
    try {
      setSessionSaving(true);
      setSessionActionError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/trade-sessions`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ userId, tradeGroupId: selectedGroupId, locationId: selectedLocationId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start trade session');
      setSelectedGroupId('');
      setSelectedLocationId('');
      if (data.adminSnapshot) {
        applyTradeDayAdminSnapshot(data.adminSnapshot);
      } else {
        await loadTradeDayAdminSnapshot(true);
      }
    } catch (err: any) {
      setSessionActionError(err.message || 'Failed to start trade session');
    } finally {
      setSessionSaving(false);
    }
  };

  const handleCancelTradeSession = async (sessionId: string) => {
    try {
      setSessionSaving(true);
      setSessionActionError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/trade-sessions/${sessionId}/cancel`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ userId, reasonCode: cancelReasonCode, reasonText: cancelReasonText }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to cancel trade session');
      if (data.adminSnapshot) {
        applyTradeDayAdminSnapshot(data.adminSnapshot);
      } else {
        await loadTradeDayAdminSnapshot(true);
      }
      setCancelDialogSessionId(null);
      setCancelReasonCode('other');
      setCancelReasonText('');
    } catch (err: any) {
      setSessionActionError(err.message || 'Failed to cancel trade session');
    } finally {
      setSessionSaving(false);
    }
  };

  const handleCompleteEvent = async () => {
    if (!eventId || !userId) return;
    const unfinishedGroups = tradeGroups.filter((group: any) => group.status !== 'completed');
    const cleanupEntries = unfinishedGroups.map((group: any) => ({
      tradeGroupId: group.id,
      reasonCode: cleanupDrafts[group.id]?.reasonCode || 'other',
      reasonText: cleanupDrafts[group.id]?.reasonText || ''
    }));
    try {
      setEventCompleting(true);
      setEventCompletionError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/complete`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ userId, cleanupEntries }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to complete event');
      setCompleteDialogOpen(false);
      window.location.reload();
    } catch (err: any) {
      setEventCompletionError(err.message || 'Failed to complete event');
    } finally {
      setEventCompleting(false);
    }
  };

  const handleRecordBehavior = async () => {
    if (!eventId || !userId || !behaviorSubjectUserId || !behaviorOutcomeType) return;
    try {
      setBehaviorSaving(true);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/behavior-records`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ actorUserId: userId, subjectUserId: behaviorSubjectUserId, sessionId: behaviorSessionId || undefined, outcomeType: behaviorOutcomeType, notes: behaviorNotes })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to record behavior outcome');
      setBehaviorNotes('');
      setBehaviorSubjectUserId('');
      setBehaviorSessionId('');
      setBehaviorRecords((current) => [data.record, ...current]);
    } catch (error: any) {
      setEventCompletionError(error.message || 'Failed to record behavior outcome');
    } finally {
      setBehaviorSaving(false);
    }
  };

  const handleRecordSessionBehavior = async () => {
    if (!eventId || !userId || !selectedSessionDetails || !sessionBehaviorSubjectUserId || !sessionBehaviorOutcomeType) return;
    try {
      setBehaviorSaving(true);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/behavior-records`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ actorUserId: userId, subjectUserId: sessionBehaviorSubjectUserId, sessionId: selectedSessionDetails.id, outcomeType: sessionBehaviorOutcomeType, notes: sessionBehaviorNotes })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to record session behavior outcome');
      setBehaviorRecords((current) => [data.record, ...current]);
      setSessionBehaviorSubjectUserId('');
      setSessionBehaviorOutcomeType('successful-handoff-confirmed');
      setSessionBehaviorNotes('');
    } catch (error: any) {
      setSessionActionError(error.message || 'Failed to record session behavior outcome');
    } finally {
      setBehaviorSaving(false);
    }
  };

  useEffect(() => {
    loadMathTradeSummary();
  }, [loadMathTradeSummary]);

  useEffect(() => {
    const loadParticipantSummaries = async () => {
      if (!eventId || !event) return;

      const participantIds = [...new Set((event.participatingUsers || []).filter(Boolean))];
      if (participantIds.length === 0) {
        setParticipantSummaries([]);
        return;
      }

      try {
        setParticipantSummaryLoading(true);
        setParticipantSummaryError(null);

        const response = await fetch(buildEventsApiUrl(`/api/users/participant-summaries?userIds=${encodeURIComponent(participantIds.join(','))}`));
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load participant list');
        }

        const summaryEntries: Array<[string, ParticipantSummary]> = (Array.isArray(data.participants) ? data.participants : []).map((participant: any) => [
          participant.userId,
          {
            userId: participant.userId,
            displayName: participant.displayName || participant.userId,
            userName: participant.userName || null,
            avatarUrl: normalizeImageUrl(participant.avatarUrl || null) || null,
            profileTag: participant.profileTag || null,
            joinedAt: participant.joinedAt || null,
            completedTradeCount: Number(participant.completedTradeCount || 0),
            reviewCount: Number(participant.reviewCount || 0),
            trustScore: participant.trustScore ?? null
          }
        ]);
        const summaryMap = new Map<string, ParticipantSummary>(summaryEntries);

        const summaries: ParticipantSummary[] = participantIds
          .map((participantId) => summaryMap.get(participantId) || ({
            userId: participantId,
            displayName: participantId,
            avatarUrl: null,
            profileTag: null,
            joinedAt: null,
            completedTradeCount: 0,
            reviewCount: 0,
            trustScore: null
          } as ParticipantSummary))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));

        setParticipantSummaries(summaries);
      } catch (participantError: any) {
        console.error('❌ Failed to load participant summaries', participantError);
        setParticipantSummaryError(participantError.message || 'Failed to load participant list');
      } finally {
        setParticipantSummaryLoading(false);
      }
    };

    loadParticipantSummaries();
  }, [eventId, event]);

  const handleJoinEvent = async () => {
    if (!isAuthenticated || !user || !event) return;

    try {
      setJoinLoading(true);
      
      const result = await dispatch(joinEventAsync({
        eventId: eventId || '',
        userId: userId || ''
      })).unwrap();

      if (result) {
        setUserHasJoined(true);
        setEvent({
          ...event,
          participatingUsers: [...(event.participatingUsers || []), userId]
        });
      }
    } catch (err: any) {
      console.error('❌ Error joining event:', err);
      setError('Failed to join event. Please try again.');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleLeaveEvent = async () => {
    if (!isAuthenticated || !user || !event) return;

    try {
      setJoinLoading(true);
      
      const result = await dispatch(leaveEventAsync({
        eventId: eventId || '',
        userId: userId || ''
      })).unwrap();

      if (result) {
        setUserHasJoined(false);
        setSelectedReminderOffsets([]);
        setReminderOptions([]);
        setEvent({
          ...event,
          participatingUsers: (event.participatingUsers || []).filter(id => id !== userId)
        });
      }
    } catch (err: any) {
      console.error('❌ Error leaving event:', err);
      setError('Failed to leave event. Please try again.');
    } finally {
      setJoinLoading(false);
    }
  };

  const buildToggledReminderOffsets = (current: number[], offset: number) => {
    const next = current.includes(offset)
      ? current.filter((value) => value !== offset)
      : [...current, offset];
    return [...new Set(next)].sort((a, b) => b - a);
  };

  const handleReminderCheckboxToggle = (offset: number) => {
    setSelectedReminderOffsets((current) => buildToggledReminderOffsets(current, offset));
  };

  const handleSaveReminderPreferences = async () => {
    if (!eventId || !userId) return;

    try {
      setReminderSaving(true);
      setReminderError(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/notification-preferences/${userId}`), {
        method: 'PUT',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ dayOffsets: selectedReminderOffsets })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save reminder preferences');
      }

      setReminderOptions(data.preferences?.availableDayOffsets || []);
      setSelectedReminderOffsets(data.preferences?.selectedDayOffsets || []);
    } catch (err: any) {
      console.error('❌ Error saving reminder preferences:', err);
      setReminderError(err.message || 'Failed to save reminder preferences');
    } finally {
      setReminderSaving(false);
    }
  };

  const getTimelineActorLabel = (userId?: string | null, displayName?: string | null) => {
    if (!userId) return null;
    const resolved = displayName || participantLookup[userId] || userId;
    if (userId === event?.ownerID) return `${resolved} (host)`;
    return resolved;
  };

  const renderTimelineUserLink = (userId?: string | null, displayName?: string | null) => {
    if (!userId) return null;
    const label = getTimelineActorLabel(userId, displayName);
    if (!label) return null;
    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => navigate(`/users/${userId}`)}
        sx={{ fontWeight: 700, textAlign: 'left' }}
      >
        {label}
      </Link>
    );
  };

  const getTimelineSummary = (entry: EventTimelineEntry) => {
    const actor = renderTimelineUserLink(entry.actorUserID, entry.actorDisplayName);
    const subject = renderTimelineUserLink(entry.subjectUserID, entry.subjectDisplayName);
    const actorLabel = getTimelineActorLabel(entry.actorUserID, entry.actorDisplayName);
    const subjectLabel = getTimelineActorLabel(entry.subjectUserID, entry.subjectDisplayName);
    const itemCount = Number(entry.metadata?.itemCount || 0);
    const itemName = entry.itemName || entry.metadata?.itemName;
    const recipientCount = Number(entry.metadata?.recipientCount || 0);

    switch (entry.type) {
      case 'event-created':
        return actor ? <>{actor} created this event.</> : <>This event was created.</>;
      case 'event-updated':
        return actor ? <>{actor} updated the event details.</> : <>Event details were updated.</>;
      case 'participant-joined':
        return actor ? <>{actor} joined the event.</> : <>A participant joined the event.</>;
      case 'participant-left':
        return actor ? <>{actor} left the event.</> : <>A participant left the event.</>;
      case 'items-added':
        return actor ? <>{actor} added {itemCount || 'some'} item{itemCount === 1 ? '' : 's'} to the event.</> : <>{itemCount || 'Some'} item{itemCount === 1 ? '' : 's'} were added to the event.</>;
      case 'item-removed':
        return actor ? <>{actor} removed {itemName || 'an item'}{subject && subjectLabel && subjectLabel !== actorLabel ? <> belonging to {subject}</> : null}.</> : <>An item was removed{itemName ? `: ${itemName}` : ''}.</>;
      case 'participants-messaged':
        return actor ? <>{actor} sent an event-wide message to {recipientCount} participant{recipientCount === 1 ? '' : 's'}.</> : <>An event-wide message was sent.</>;
      case 'math-trade-preview-run':
        return actor ? <>{actor} generated a math trade preview.</> : <>A math trade preview was generated.</>;
      case 'math-trade-published':
        return actor ? <>{actor} published the current math trade preview.</> : <>The current math trade preview was published.</>;
      case 'math-trade-finalized':
        return actor ? <>{actor} finalized the math trade results.</> : <>The math trade results were finalized.</>;
      default:
        return <>{entry.message}</>;
    }
  };

  const getTimelineMetadataChips = (entry: EventTimelineEntry) => {
    const chips: string[] = [];
    if (entry.metadata?.subject) chips.push(`Subject: ${entry.metadata.subject}`);
    if (entry.metadata?.itemName) chips.push(`Item: ${entry.metadata.itemName}`);
    if (entry.metadata?.itemCount) chips.push(`Items: ${entry.metadata.itemCount}`);
    if (entry.metadata?.participantCount) chips.push(`Participants: ${entry.metadata.participantCount}`);
    if (entry.metadata?.recipientCount !== undefined) chips.push(`Recipients: ${entry.metadata.recipientCount}`);
    if (entry.metadata?.numTrades) chips.push(`Trades: ${entry.metadata.numTrades}`);
    if (entry.metadata?.numGroups) chips.push(`Groups: ${entry.metadata.numGroups}`);
    if (entry.metadata?.eventName) chips.push(`Event: ${entry.metadata.eventName}`);
    if (entry.metadata?.category) chips.push(`Category: ${entry.metadata.category}`);
    if (entry.metadata?.removalReason) chips.push(`Reason: ${entry.metadata.removalReason}`);
    return chips;
  };

  const handleSendParticipantMessage = async () => {
    if (!eventId || !userId) return;

    try {
      setParticipantMessageSaving(true);
      setParticipantMessageError(null);
      setParticipantMessageSuccess(null);
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/message-participants`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({
          senderUserId: userId,
          subject: participantMessageSubject.trim(),
          body: participantMessageBody.trim()
        })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send participant message');
      }

      setParticipantMessageSuccess(data.message || 'Message sent to participants.');
      setParticipantMessageSubject('');
      setParticipantMessageBody('');
    } catch (err: any) {
      console.error('? Error messaging participants:', err);
      setParticipantMessageError(err.message || 'Failed to send participant message');
    } finally {
      setParticipantMessageSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper elevation={8} sx={{ p: 4, borderRadius: '16px', textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#667eea', mb: 2 }} size={60} />
          <Typography variant="h6" sx={{ color: '#495057' }}>
            Loading event details...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        pt: 4
      }}>
        <Container maxWidth="lg">
          <Button
            onClick={() => navigate('/')}
            startIcon={<ArrowBackIcon />}
            sx={{ 
              mb: 3, 
              color: 'white',
              fontWeight: '600',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Events
          </Button>
          
          <Paper elevation={8} sx={{ p: 4, borderRadius: '16px', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 2, color: '#e74c3c' }}>
              😔 Event Not Found
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#6c757d' }}>
              {error || 'The event you\'re looking for doesn\'t exist.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                backgroundColor: '#667eea',
                '&:hover': { backgroundColor: '#5a67d8' },
                borderRadius: '25px',
                textTransform: 'none',
                fontWeight: '600'
              }}
            >
              Browse Events
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const isCreator = event.ownerID === userId;
  const isSiteAdmin = isSiteAdminUser(user as any);
  const canManageTradeDay = isCreator || isSiteAdmin;
  const isMember = userHasJoined;
  const canManageReminders = Boolean(userId && (isCreator || isMember));
  const eventDateLabel = formatEventDateTime(event.eventDateTime);
  const cutoffSource = event.tradeCutoffDate || event.tradeDateTime;
  const tradeCutoffLabel = formatEventDateTime(cutoffSource);
  const mustArriveByLabel = formatEventDateTime(event.mustArriveByDateTime || null);

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      pb: 6
    }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        
        {/* Back Button */}
        <Button
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{ 
            mb: 4, 
            color: 'white',
            fontWeight: '600',
            '&:hover': { 
              backgroundColor: 'rgba(255,255,255,0.1)',
              transform: 'translateX(-4px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Back to Events
        </Button>

        <Grid container spacing={4}>
          
          {/* Event Details Card */}
          <Grid item xs={12}>
            <Card sx={{ 
              borderRadius: '20px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                
                {/* Event Title */}
                <Typography variant="h3" component="h1" sx={{ 
                  fontWeight: '700',
                  mb: 3,
                  color: '#333',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                }}>
                  {event.eventName || 'Unnamed Event'}
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(_, value: EventDetailsTab) => setActiveTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                  >
                    <Tab value="overview" label="Overview" />
                    {canManageTradeDay && <Tab value="trade-day" label="Event Admin" />}
                  </Tabs>
                </Box>

                {activeTab === 'overview' && (
                  <>

                <EventInfoGrid
                  event={event}
                  eventDateLabel={eventDateLabel}
                  tradeCutoffLabel={tradeCutoffLabel}
                  mustArriveByLabel={mustArriveByLabel}
                />

                {/* Event Description */}
                {event.description && (
                  <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', mb: 4, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ fontWeight: '600', mb: 1, color: '#333' }}>
                      About This Event
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      lineHeight: '1.7',
                      color: '#495057',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {event.description}
                    </Typography>
                  </Paper>
                )}

                <EventParticipantsPanel
                  ownerId={event.ownerID}
                  participants={participantSummaries}
                  expanded={participantsExpanded}
                  loading={participantSummaryLoading}
                  error={participantSummaryError}
                  onToggle={() => setParticipantsExpanded((current) => !current)}
                  onOpenProfile={(userId) => navigate(`/users/${userId}`)}
                  formatJoinedDate={formatJoinedDate}
                />

                {tradeCutoffPassed && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Trade cutoff has passed. Joining, leaving, and event changes are locked.
                  </Alert>
                )}

                    {/* Join/Leave Button */}
                    {isAuthenticated && !isCreator && (
                      <Box sx={{ mb: 3 }}>
                        {userHasJoined ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="large"
                            onClick={handleLeaveEvent}
                            disabled={joinLoading || tradeCutoffPassed}
                            sx={{ 
                              borderRadius: '25px',
                              textTransform: 'none',
                              fontWeight: '600'
                            }}
                          >
                            {joinLoading ? 'Leaving...' : '👋 Leave Event'}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="large"
                            onClick={handleJoinEvent}
                            disabled={joinLoading || tradeCutoffPassed}
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '25px',
                              textTransform: 'none',
                              fontWeight: '600'
                            }}
                          >
                            {joinLoading ? 'Joining...' : '🎉 Join Event'}
                          </Button>
                        )}
                      </Box>
                    )}

                    {isCreator && (
                      <Box sx={{ mb: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/edit-event/${eventId}`)}
                          disabled={tradeCutoffPassed}
                          sx={{ textTransform: 'none', fontWeight: '600' }}
                        >
                          Edit Event
                        </Button>
                      </Box>
                    )}

                    {canManageReminders && (
                      <EventRemindersPanel
                        availableOffsets={reminderOptions}
                        selectedOffsets={selectedReminderOffsets}
                        saving={reminderSaving}
                        error={reminderError}
                        onToggleOffset={handleReminderCheckboxToggle}
                        onSave={handleSaveReminderPreferences}
                      />
                    )}

                    {mathTradeSummary && <MathTradeSnapshot summary={mathTradeSummary} />}

                    {eventId && (
                      <Box sx={{ mb: 3 }}>
                        <MathTradeRunPanel
                          eventId={eventId}
                          canRun={Boolean(canManageTradeDay)}
                          userId={userId || undefined}
                        />
                      </Box>
                    )}

                    {userId && (
                      <Box sx={{ mb: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/events/${eventId}/my-wantlists`)}
                          sx={{ textTransform: 'none', fontWeight: '600' }}
                        >
                          {tradeCutoffPassed ? 'View My Want Lists' : 'Edit My Want Lists'}
                        </Button>
                        {tradeCutoffPassed && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Want lists are read-only after the cutoff.
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Paper elevation={2} sx={{ p: 4, borderRadius: '12px' }}>
                      <EventItemsSection
                        eventId={eventId || ''}
                        userId={userId || undefined}
                        isCreator={isCreator}
                        isMember={isMember}
                        eventCategory={event.category || ''}
                        eventName={event.eventName || ''}
                        tradeCutoffPassed={tradeCutoffPassed}
                        eventItems={eventItems}
                        itemsLoading={eventItemsLoading}
                        itemsError={eventItemsError}
                        onRefreshItems={loadEventItems}
                        onItemsUpdate={handleItemsUpdated}
                      />
                    </Paper>
                  </>
                )}

                {canManageTradeDay && activeTab === 'trade-day' && (
                  <>
                    <Stack spacing={3}>
                      <EventAdminSummaryPanel
                        stats={[
                          { label: 'Ready groups', value: eventDaySummary.readyGroups, severity: 'success' },
                          { label: 'Waiting groups', value: eventDaySummary.waitingGroups, severity: 'info' },
                          { label: 'Available locations', value: locations.filter((location) => location.active && !location.currentSessionId).length, severity: 'success' },
                          { label: 'Occupied locations', value: locations.filter((location) => Boolean(location.currentSessionId)).length, severity: 'warning' },
                          { label: 'Staged sessions', value: eventDaySummary.stagedSessions, severity: 'warning' },
                          { label: 'Active sessions', value: eventDaySummary.activeSessions, severity: 'success' },
                          { label: 'Issues logged', value: eventDaySummary.behaviorRecords, severity: 'info' },
                          { label: 'No-shows', value: eventDaySummary.noShows, severity: 'warning' },
                        ]}
                      />

                      <EventWatchListPanel alerts={eventDayAlerts} />

                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                          <EventTradeGroupsPanel
                            groups={actionableTradeGroups}
                            loading={tradeGroupsLoading}
                            error={suppressTradeGroupsOwnerOnlyError ? null : tradeGroupsError}
                            onOpenDetails={(group) => setSelectedTradeGroupDetails(group as EventTradeGroupRecord)}
                            describeBlocker={(group) => describeTradeGroupBlocker(group as EventTradeGroupRecord)}
                          />
                        </Grid>

                        <Grid item xs={12} lg={6}>
                          <Box sx={{ height: '100%' }}>
                            <EventLocationsManager
                              eventId={eventId || ''}
                              eventName={event?.eventName || event?.name || 'Event'}
                              userId={userId}
                              disabled={sessionSaving}
                              initialLocations={locations as any}
                              onLocationsChanged={handleLocationsChanged as any}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                          <EventStartSessionPanel
                            error={sessionActionError}
                            saving={sessionSaving}
                            selectedGroupId={selectedGroupId}
                            selectedLocationId={selectedLocationId}
                            readyGroups={tradeGroups.filter((group) => group.readinessState === 'ready-to-start-trade')}
                            locations={suggestedLocations}
                            selectedGroupParticipantCount={selectedTradeGroup?.participantCount}
                            onGroupChange={setSelectedGroupId}
                            onLocationChange={setSelectedLocationId}
                            onStart={handleStartTradeSession}
                          />
                        </Grid>

                        <Grid item xs={12} lg={6}>
                          <EventSessionsPanel
                            saving={sessionSaving}
                            sessions={activeSessions.map((session) => ({
                              id: session.id,
                              locationName: session.locationName,
                              status: session.status,
                              participantCount: session.participantUserIds?.length || 0,
                              initiatedLabel: formatEventDateTime(session.initiatedAt),
                              initiatedElapsed: formatElapsed(session.initiatedAt),
                              startedElapsed: formatElapsed(session.startedAt),
                              cancelReason: session.cancelReasonCode || session.cancelReasonText ? `${session.cancelReasonCode || 'other'}${session.cancelReasonText ? ` — ${session.cancelReasonText}` : ''}` : undefined,
                              behaviorRecords: behaviorRecords.filter((record) => record.sessionId === session.id).map((record) => ({ id: record.id, text: `${participantLookup[record.subjectUserId] || record.subjectUserId}: ${record.outcomeType}${record.notes ? ` — ${record.notes}` : ''}` })),
                            }))}
                            onOpenDetails={(sessionId) => {
                              const session = activeSessions.find((candidate) => candidate.id === sessionId);
                              if (session) { setSessionDetailTab(0); setSelectedSessionDetails(session); }
                            }}
                            onCancel={setCancelDialogSessionId}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <EventLocationsOverviewPanel
                            locations={locations.map((location) => {
                              const linkedSession = activeSessions.find((session) => session.id === location.currentSessionId);
                              return {
                                id: location.id,
                                name: location.name,
                                capacity: location.capacity,
                                active: location.active,
                                occupied: Boolean(location.currentSessionId),
                                occupiedAtLabel: formatEventDateTime(location.occupiedAt),
                                sessionId: linkedSession?.id,
                                sessionStatus: linkedSession?.status,
                                sessionStartedLabel: formatEventDateTime(linkedSession?.initiatedAt),
                                participants: linkedSession ? (linkedSession.participantUserIds || []).map((participantId) => participantLookup[participantId] || participantId).join(', ') : undefined,
                                tradeGroupId: linkedSession?.tradeGroupId,
                              };
                            })}
                            onOpenSession={(sessionId) => {
                              const session = activeSessions.find((candidate) => candidate.id === sessionId);
                              if (session) { setSessionDetailTab(0); setSelectedSessionDetails(session); }
                            }}
                            onOpenLocation={(locationId) => {
                              const location = locations.find((candidate) => candidate.id === locationId);
                              if (location) setSelectedLocationDetails(location);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6} xl={3}>
                              <EventCheckInQrPanel
                                eventName={event.eventName}
                                targetUrl={eventQrTargetUrl}
                                dataUrl={eventQrDataUrl}
                                expanded={eventQrExpanded}
                                onToggle={() => setEventQrExpanded((current) => !current)}
                                onPrint={handleOpenPrintableQr}
                                onDownload={handleDownloadEventQr}
                              />
                            </Grid>

                            <Grid item xs={12} md={6} xl={3}>
                              <EventTimelinePanel
                                entries={filteredTimelineEntries}
                                filter={timelineFilter}
                                expanded={timelineExpanded}
                                loading={timelineLoading}
                                error={timelineError}
                                onToggle={() => setTimelineExpanded((current) => !current)}
                                onFilterChange={(filter) => setTimelineFilter(filter as TimelineFilter)}
                                typeLabel={(type) => timelineTypeLabels[type] || type}
                                formatDate={formatEventDateTime}
                                renderSummary={getTimelineSummary}
                                metadataChips={getTimelineMetadataChips}
                              />
                            </Grid>

                            <Grid item xs={12} md={6} xl={3}>
                              <EventBehaviorPanel
                                expanded={behaviorExpanded}
                                sessionId={behaviorSessionId}
                                subjectUserId={behaviorSubjectUserId}
                                outcomeType={behaviorOutcomeType}
                                notes={behaviorNotes}
                                saving={behaviorSaving}
                                sessions={activeSessions.map((session) => ({ id: session.id, label: `${session.locationName || 'Assigned location'} — ${session.status} — ${formatEventDateTime(session.initiatedAt)}` }))}
                                participants={(behaviorSessionId
                                  ? participantSummaries.filter((participant) => activeSessions.find((session) => session.id === behaviorSessionId)?.participantUserIds?.includes(participant.userId))
                                  : participantSummaries
                                ).map((participant) => ({ id: participant.userId, label: participant.displayName || participant.userId }))}
                                records={behaviorRecords.map((record) => {
                                  const relatedSession = activeSessions.find((session) => session.id === record.sessionId);
                                  return {
                                    id: record.id,
                                    subjectLabel: participantLookup[record.subjectUserId] || record.subjectUserId,
                                    outcomeType: record.outcomeType,
                                    dateLabel: formatEventDateTime(record.createdAt),
                                    sessionLabel: record.sessionId ? (relatedSession ? `${relatedSession.locationName || 'Assigned location'} — ${relatedSession.status} — ${formatEventDateTime(relatedSession.initiatedAt)}` : record.sessionId) : undefined,
                                    notes: record.notes,
                                  };
                                })}
                                onToggle={() => setBehaviorExpanded((current) => !current)}
                                onSessionChange={(value) => { setBehaviorSessionId(value); setBehaviorSubjectUserId(''); }}
                                onSubjectChange={setBehaviorSubjectUserId}
                                onOutcomeChange={setBehaviorOutcomeType}
                                onNotesChange={setBehaviorNotes}
                                onRecord={handleRecordBehavior}
                              />
                            </Grid>

                            <Grid item xs={12} md={6} xl={3}>
                              <EventMessagingPanel
                                expanded={messagingExpanded}
                                subject={participantMessageSubject}
                                body={participantMessageBody}
                                saving={participantMessageSaving}
                                error={participantMessageError}
                                success={participantMessageSuccess}
                                onToggle={() => setMessagingExpanded((current) => !current)}
                                onSubjectChange={setParticipantMessageSubject}
                                onBodyChange={setParticipantMessageBody}
                                onSend={handleSendParticipantMessage}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <EventCompletionPanel
                            expanded={completionExpanded}
                            unfinishedGroupCount={tradeGroups.filter((group: any) => group.status !== 'completed').length}
                            completing={eventCompleting}
                            error={eventCompletionError}
                            onToggle={() => setCompletionExpanded((current) => !current)}
                            onOpenConfirmation={() => setCompleteDialogOpen(true)}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <EventDetailsDialog open={Boolean(selectedSessionDetails)} title="Trade Session Details" onClose={() => setSelectedSessionDetails(null)}>
            {selectedSessionDetails && (
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedSessionDetails.locationName || 'Assigned location'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {selectedSessionDetails.status} • Initiated: {formatEventDateTime(selectedSessionDetails.initiatedAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Started: {formatEventDateTime(selectedSessionDetails.startedAt || undefined)} • Completed: {formatEventDateTime(selectedSessionDetails.completedAt || undefined)}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label={`Trade Group: ${selectedSessionDetails.tradeGroupId}`} onClick={() => {
                    const relatedGroup = tradeGroups.find((group) => group.id === selectedSessionDetails.tradeGroupId);
                    if (relatedGroup) {
                      setSelectedSessionDetails(null);
                      setSelectedTradeGroupDetails(relatedGroup);
                    }
                  }} clickable />
                  <Chip label={`Location: ${selectedSessionDetails.locationName || 'Assigned location'}`} onClick={() => {
                    const relatedLocation = locations.find((location) => location.name === selectedSessionDetails.locationName || location.currentSessionId === selectedSessionDetails.id);
                    if (relatedLocation) {
                      setSelectedSessionDetails(null);
                      setSelectedLocationDetails(relatedLocation);
                    }
                  }} clickable />
                </Stack>
                {(selectedSessionDetails.status === 'staged' || selectedSessionDetails.status === 'active') && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button color="warning" variant="outlined" onClick={() => {
                      setCancelDialogSessionId(selectedSessionDetails.id);
                      setSelectedSessionDetails(null);
                    }}>
                      Cancel This Session
                    </Button>
                  </Stack>
                )}
                <Tabs value={sessionDetailTab} onChange={(_, value) => setSessionDetailTab(value)}>
                  <Tab label="Participants" />
                  <Tab label="Behavior" />
                  <Tab label="Reviews" />
                </Tabs>
                {sessionDetailTab === 0 && <>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Participants</Typography>
                  <Stack spacing={0.75}>
                    {(selectedSessionDetails.participantUserIds || []).map((participantId) => (
                      <Typography key={participantId} variant="body2">
                        {participantLookup[participantId] || participantId}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Participant state</Typography>
                  <Stack spacing={1}>
                    {(selectedSessionDetails.participantStates || []).map((state) => {
                      const reviewAssignment = (selectedSessionDetails.reviewAssignments || []).find((assignment) => assignment.userId === state.userId);
                      const submittedReview = eventReviews.find((review) => review.sessionId === selectedSessionDetails.id && review.reviewerUserId === state.userId);
                      return (
                        <Paper key={state.userId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {participantLookup[state.userId] || state.userId}
                              </Typography>
                              <Button size="small" variant="text" onClick={() => navigate(`/users/${state.userId}`)}>
                                View Profile
                              </Button>
                            </Stack>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              <Chip size="small" color={state.locationCheckedInAt ? 'success' : 'default'} label={state.locationCheckedInAt ? 'Arrived' : 'Not arrived'} />
                              <Chip size="small" color={state.completionConfirmedAt ? 'success' : 'default'} label={state.completionConfirmedAt ? 'Completed' : 'Not completed'} />
                              <Chip size="small" color={state.issueReportedAt ? 'warning' : 'default'} label={state.issueReportedAt ? 'Issue reported' : 'No issue'} />
                              <Chip size="small" color={submittedReview ? 'info' : 'default'} label={submittedReview ? 'Review submitted' : 'Review pending'} />
                            </Stack>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Location check-in: {formatEventDateTime(state.locationCheckedInAt || undefined) || 'Not yet'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Completion confirmed: {formatEventDateTime(state.completionConfirmedAt || undefined) || 'Not yet'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Issue reported: {formatEventDateTime(state.issueReportedAt || undefined) || 'No'}
                          </Typography>
                          {state.issueReasonCode && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Issue reason: {state.issueReasonCode}{state.issueReasonText ? ` — ${state.issueReasonText}` : ''}
                            </Typography>
                          )}
                          {reviewAssignment && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Review target: {participantLookup[reviewAssignment.reviewTargetUserId || ''] || reviewAssignment.reviewTargetUserId || 'Unknown'} • Give: {reviewAssignment.giveItem || 'Unknown'} • Receive: {reviewAssignment.receiveItem || 'Unknown'}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Review submitted: {submittedReview ? `${submittedReview.rating}/5 on ${formatEventDateTime(submittedReview.createdAt) || 'Unknown date'}` : 'Not yet'}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Paper>
                </>}
                {sessionDetailTab === 1 && <>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Behavior records</Typography>
                  <Stack spacing={0.75}>
                    {behaviorRecords.filter((record) => record.sessionId === selectedSessionDetails.id).length > 0 ? behaviorRecords.filter((record) => record.sessionId === selectedSessionDetails.id).map((record) => (
                      <Typography key={record.id} variant="body2" color="text.secondary">
                        {participantLookup[record.subjectUserId] || record.subjectUserId}: {record.outcomeType}{record.notes ? ` — ${record.notes}` : ''}
                      </Typography>
                    )) : <Typography variant="body2" color="text.secondary">No behavior records for this session.</Typography>}
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Add behavior record to this session</Typography>
                  <Stack spacing={1.5}>
                    <TextField select label="Participant" value={sessionBehaviorSubjectUserId} onChange={(e) => setSessionBehaviorSubjectUserId(e.target.value)} fullWidth>
                      {(selectedSessionDetails.participantUserIds || []).map((participantId) => (
                        <MenuItem key={participantId} value={participantId}>{participantLookup[participantId] || participantId}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label="Outcome type" value={sessionBehaviorOutcomeType} onChange={(e) => setSessionBehaviorOutcomeType(e.target.value)} fullWidth>
                      {['successful-handoff-confirmed', 'no-show', 'late-arrival', 'wrong-item-delivered', 'item-condition-dispute', 'admin-resolved-issue'].map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                    <TextField label="Notes" value={sessionBehaviorNotes} onChange={(e) => setSessionBehaviorNotes(e.target.value)} multiline minRows={2} fullWidth />
                    <Box>
                      <Button variant="contained" onClick={handleRecordSessionBehavior} disabled={behaviorSaving || !sessionBehaviorSubjectUserId}>Record Session Behavior</Button>
                    </Box>
                  </Stack>
                </Paper>
                </>}
                {sessionDetailTab === 2 && <>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Review status</Typography>
                  <Stack spacing={1}>
                    {(selectedSessionDetails.participantUserIds || []).map((participantId) => {
                      const submittedReview = eventReviews.find((review) => review.sessionId === selectedSessionDetails.id && review.reviewerUserId === participantId);
                      const assignment = (selectedSessionDetails.reviewAssignments || []).find((reviewAssignment) => reviewAssignment.userId === participantId);
                      return (
                        <Paper key={participantId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {participantLookup[participantId] || participantId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Review target: {participantLookup[assignment?.reviewTargetUserId || ''] || assignment?.reviewTargetUserId || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Review submitted: {submittedReview ? `${submittedReview.rating}/5 on ${formatEventDateTime(submittedReview.createdAt) || 'Unknown date'}` : 'Not yet'}
                          </Typography>
                          {submittedReview?.comment && (
                            <Typography variant="body2" color="text.secondary">
                              {submittedReview.comment}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Paper>
                </>}
              </Stack>
            )}
        </EventDetailsDialog>

        <EventDetailsDialog open={Boolean(selectedTradeGroupDetails)} title="Trade Group Details" onClose={() => setSelectedTradeGroupDetails(null)}>
            {selectedTradeGroupDetails && (
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Group {selectedTradeGroupDetails.groupKey}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Readiness: {selectedTradeGroupDetails.readinessState}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Participants: {selectedTradeGroupDetails.participantCount} • Arrived: {selectedTradeGroupDetails.arrivedCount} • Busy: {selectedTradeGroupDetails.busyCount}
                </Typography>
                <Alert severity={selectedTradeGroupDetails.readinessState === 'ready-to-start-trade' ? 'success' : selectedTradeGroupDetails.readinessState === 'ready-when-other-members-finish-other-trades' ? 'warning' : 'info'}>
                  {describeTradeGroupBlocker(selectedTradeGroupDetails)}
                </Alert>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {activeSessions.filter((session) => session.tradeGroupId === selectedTradeGroupDetails.id).map((session) => (
                    <Chip key={session.id} label={`Session: ${session.locationName || 'Assigned location'} (${session.status})`} onClick={() => {
                      setSelectedTradeGroupDetails(null);
                      setSessionDetailTab(0);
                      setSelectedSessionDetails(session);
                    }} clickable />
                  ))}
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Participants</Typography>
                  <Stack spacing={0.75}>
                    {(selectedTradeGroupDetails.participantUserIds || []).map((participantId) => {
                      const participantState = (selectedTradeGroupDetails.participantStates || []).find((participant) => participant.userId === participantId);
                      const blockingSession = activeSessions.find((session) => session.id === participantState?.activeSessionId) || activeSessions.find((session) => session.status !== 'completed' && session.status !== 'canceled' && (session.participantUserIds || []).includes(participantId));
                      const arrived = Boolean(participantState?.checkedInAt);
                      const busy = participantState?.currentAvailability === 'busy' || Boolean(blockingSession);
                      return (
                        <Paper key={participantId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {participantLookup[participantId] || participantId}
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              <Chip size="small" color={arrived ? 'success' : 'default'} label={arrived ? 'Arrived' : 'Not arrived'} />
                              <Chip size="small" color={busy ? 'warning' : 'success'} label={busy ? 'Busy' : 'Available'} />
                            </Stack>
                          </Stack>
                          {participantState?.checkedInAt && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Checked in: {formatEventDateTime(participantState.checkedInAt)}
                            </Typography>
                          )}
                          {blockingSession && (
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                              <Typography variant="caption" color="text.secondary">
                                In session: {blockingSession.locationName || 'Assigned location'} • {blockingSession.status}
                              </Typography>
                              <Button size="small" variant="text" onClick={() => {
                                setSelectedTradeGroupDetails(null);
                                setSessionDetailTab(0);
                                setSelectedSessionDetails(blockingSession);
                              }}>
                                Open Blocking Session
                              </Button>
                            </Stack>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Paper>
                <TradeGroupPathsPanel paths={(selectedTradeGroupDetails.tradeEdges || []).map((edge) => `${participantLookup[edge.fromLabel || ''] || edge.fromLabel || 'Unknown'} gives ${edge.giveItem || 'Unknown'} and receives ${edge.receiveItem || 'Unknown'} from ${participantLookup[edge.receiveLabel || ''] || edge.receiveLabel || 'Unknown'}`)} />
                {selectedTradeGroupDetails.readinessState === 'ready-to-start-trade' && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      This group is ready to start. Use the Start Trade Session panel to assign it to an available location.
                    </Typography>
                    <Button variant="contained" onClick={() => {
                      setSelectedGroupId(selectedTradeGroupDetails.id);
                      setSelectedTradeGroupDetails(null);
                      window.scrollTo({ top: document.body.scrollHeight / 2, behavior: 'smooth' });
                    }}>Close and Start Session</Button>
                  </Paper>
                )}
              </Stack>
            )}
        </EventDetailsDialog>

        <EventDetailsDialog open={Boolean(selectedLocationDetails)} title="Location Details" onClose={() => setSelectedLocationDetails(null)}>
            {selectedLocationDetails && (() => {
              const linkedSession = activeSessions.find((session) => session.id === selectedLocationDetails.currentSessionId);
              const linkedGroup = linkedSession ? tradeGroups.find((group) => group.id === linkedSession.tradeGroupId) : null;
              return (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedLocationDetails.name}</Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`Capacity: ${selectedLocationDetails.capacity}`} />
                    <Chip label={selectedLocationDetails.active ? 'Active' : 'Inactive'} color={selectedLocationDetails.active ? 'success' : 'default'} />
                    <Chip label={selectedLocationDetails.currentSessionId ? 'Occupied' : 'Available'} color={selectedLocationDetails.currentSessionId ? 'warning' : 'success'} />
                  </Stack>
                  {selectedLocationDetails.occupiedAt && (
                    <Typography variant="body2" color="text.secondary">
                      Occupied since: {formatEventDateTime(selectedLocationDetails.occupiedAt)}
                    </Typography>
                  )}
                  {linkedSession && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Linked session</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {linkedSession.locationName || selectedLocationDetails.name} • {linkedSession.status}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Participants: {(linkedSession.participantUserIds || []).map((participantId) => participantLookup[participantId] || participantId).join(', ')}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="text" onClick={() => { setSelectedLocationDetails(null); setSessionDetailTab(0); setSelectedSessionDetails(linkedSession); }}>Open Session</Button>
                        {linkedGroup && <Button variant="text" onClick={() => { setSelectedLocationDetails(null); setSelectedTradeGroupDetails(linkedGroup); }}>Open Trade Group</Button>}
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              );
            })()}
        </EventDetailsDialog>

        <EventCancelSessionDialog
          open={Boolean(cancelDialogSessionId)}
          reasonCode={cancelReasonCode}
          reasonText={cancelReasonText}
          saving={sessionSaving}
          onClose={() => setCancelDialogSessionId(null)}
          onReasonCodeChange={setCancelReasonCode}
          onReasonTextChange={setCancelReasonText}
          onConfirm={() => cancelDialogSessionId && handleCancelTradeSession(cancelDialogSessionId)}
        />

        <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Complete Event</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Record cleanup reasons for any unfinished groups before closing the event.
              </Typography>
              {tradeGroups.filter((group: any) => group.status !== 'completed').map((group: any) => (
                <Paper key={group.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Group {group.groupKey}
                    </Typography>
                    <TextField select label="Cleanup reason" value={cleanupDrafts[group.id]?.reasonCode || 'other'} onChange={(e) => setCleanupDrafts((current) => ({ ...current, [group.id]: { reasonCode: e.target.value, reasonText: current[group.id]?.reasonText || '' } }))} fullWidth>
                      {['participant-no-show', 'ran-out-of-time', 'item-rejected', 'other'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                    </TextField>
                    <TextField label="Details" value={cleanupDrafts[group.id]?.reasonText || ''} onChange={(e) => setCleanupDrafts((current) => ({ ...current, [group.id]: { reasonCode: current[group.id]?.reasonCode || 'other', reasonText: e.target.value } }))} multiline minRows={2} fullWidth />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompleteDialogOpen(false)}>Close</Button>
            <Button color="warning" variant="contained" onClick={handleCompleteEvent} disabled={eventCompleting}>Confirm Event Completion</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default EventDetailsEnhanced;


